import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormHelperText, Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getCompanyGDPRSettingsByLoggedineRecruiter,
  DncLogSearchFilterDTO,
  getDncLogByFilter,
  IDncLog,
  CompanyGdprSettingsDTO,
  IDnc,
  uploadFileToS3,
  saveCandidateDNCSettings,
  CandidateDataDeleteRequestDTO,
  candidateDataDeleteRequest,
  IDncByTeam,
  getCompanyGDPRSettingsByRecruiter,
  DncDetailsDTO,
  LogAction,
  LogContextType,
  saveActivityLog,
} from 'wisestep-api';
import {
  ControlledTextArea,
  FileTypeVariants,
  Label,
  WsButton,
  WsButtonGroup,
  WsFileUpload,
  WsTypoColors,
  WsTypography,
  WsDatePicker,
} from 'wisestep-base';
import * as Yup from 'yup';

import { useWorkspaceContext } from '../../../context';
import { defaultLeadActivityLogDTO } from '../../../features/leads/helper/leadHelper';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import DNCLogs from './DNCLogs';

const defaultPrivacyOptions = new Map();
defaultPrivacyOptions.set('Open for Communication', 'open');
defaultPrivacyOptions.set('Right to Forget', 'forget');
defaultPrivacyOptions.set('Right to Restrict', 'restrict');
defaultPrivacyOptions.set('Right to Erase', 'delete');

interface DataPrivacyProps {
  email: string;
  candidateName: string;
  isInDnc: boolean;
  dncDetails?: IDncByTeam | null;
  privacyOptions?: Map<string, string>;
  restrictLabel?: string;
  onSubmitApi?: (data: DncDetailsDTO) => Promise<any>;
  entityId?: string;
  entityName?: string;
  logContextType?: LogContextType;
  parentEntitiyId?: string;
  enableActivityLog?: boolean;
  clientContactId?: string;
  clientContactParentId?: string;
  clientContactName?: string;
}

interface IDataPrivacyState {
  logs: IDncLog[];
  settings: CompanyGdprSettingsDTO | null;
  file?: File | null;
}
const formValidation = {
  dataPrivacyOption: Yup.string().required('Select a DataPrivacy Option'),
  attachmentUrl: Yup.string().required('Please upload a file'),
  dncEndDate: Yup.date()
    .nullable()
    .when('dataPrivacyOption', {
      is: 'restrict',
      then: Yup.date().required('Please Select Restrict till date'),
    }),
  notes: Yup.string().required('Enter comment'),
};

const DataPrivacy = ({
  email,
  candidateName,
  dncDetails,
  privacyOptions,
  restrictLabel,
  onSubmitApi,
  entityId,
  entityName,
  logContextType,
  parentEntitiyId,
  enableActivityLog = false,
  clientContactId,
  clientContactName,
  clientContactParentId,
}: DataPrivacyProps) => {
  const effectivePrivacyOptions = privacyOptions || defaultPrivacyOptions;

  const defaultPrivacyOptionValue =
    effectivePrivacyOptions.size === 1
      ? Array.from(effectivePrivacyOptions.values())[0]
      : dncDetails?.dataPrivacyOption || 'open';
  const methods = useForm<IDnc>({
    defaultValues: {
      dataPrivacyOption: defaultPrivacyOptionValue,
      dncEndDate: dncDetails?.dncEndDate,
    },
    resolver: yupResolver(Yup.object().shape(formValidation)),
  });
  const errors = methods.formState.errors;
  const selectedOption = methods.watch('dataPrivacyOption');
  const [dataPrivacyState, setDataPrivacyState] = useState<IDataPrivacyState>({
    logs: [],
    settings: null,
  });
  const workSpace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const options = privacyOptions || defaultPrivacyOptions;
  const submitApi = onSubmitApi || saveCandidateDNCSettings;

  const setDataPrivacyOptions = (index: number, value: string) => {
    methods.setValue('dataPrivacyOption', options.get(value));
  };

  const onFileUpload = async (files: File[]) => {
    try {
      const response = await uploadFileToS3(files[0]);
      methods.setValue('attachmentUrl', response.split('?')[0]);
      setDataPrivacyState((previousData) => ({ ...previousData, file: files[0] }));
      setNotification(success('File Upload Success!'));
    } catch {
      setNotification(failed('File Upload Failed!'));
    }
  };
  const submit = async (formValues: IDnc) => {
    const oldOption = dncDetails?.dataPrivacyOption ?? 'open';
    const newOption = formValues.dataPrivacyOption;
    const request: IDnc = {
      ...formValues,
      isInDnc: selectedOption !== 'open',
      action: selectedOption === 'open' ? 'Remove' : 'Add',
      contactLink: email,
      isAddedByCandidate: false,
      workspaceId: workSpace.id ?? '',
    };
    try {
      await submitApi(request as DncDetailsDTO & IDnc);
      if (request.dataPrivacyOption === 'delete') {
        const settingsResponse = await getCompanyGDPRSettingsByRecruiter(workSpace.ownerId ?? '');
        const gdprId = settingsResponse.data?.id;
        const deleteRequestEntity: CandidateDataDeleteRequestDTO = {
          email: request.contactLink,
          companyCandidateGdprSettingsId: gdprId ?? dataPrivacyState.settings?.id,
          isProcessed: false,
          workspaceId: request.workspaceId ?? '',
        };
        await candidateDataDeleteRequest(deleteRequestEntity);
      }
      if (enableActivityLog && oldOption !== newOption) {
        if (logContextType === LogContextType.Client && entityId === parentEntitiyId) {
          const action =
            oldOption === 'open' && newOption !== 'open' ? LogAction.Add : LogAction.Edit;

          const description =
            oldOption === 'open' && newOption !== 'open' ? 'Marked as DNC' : 'Removed from DNC';

          await saveActivityLog({
            ...defaultLeadActivityLogDTO,
            contextData: {},
            action,
            field: 'Client DNC Settings',
            description,
            entityId: entityId ?? '',
            entityName: entityName ?? '',
            parentEntitiyType: logContextType,
            parentEntitiyId: parentEntitiyId ?? '',
          });
        } else {
          let description = '';
          let action: LogAction = LogAction.Edit;

          if (oldOption === 'open' && newOption !== 'open') {
            description =
              logContextType === LogContextType.Lead
                ? `Marked Contact as DNC and CLOSED Lead for ${entityName}`
                : `Marked Contact as DNC for Client Contact ${entityName} under`;
            action = LogAction.Add;
          } else if (oldOption !== newOption) {
            description =
              logContextType === LogContextType.Lead
                ? `Removed DNC details from and OPENED Lead for ${entityName}`
                : `Removed DNC from Client Contact ${entityName} under`;
            action = LogAction.Edit;
          }

          const fieldValue =
            logContextType === LogContextType.Lead ? 'Lead DNC Setting' : 'Client Contact';
          const logPayload = {
            ...defaultLeadActivityLogDTO,
            contextData: {},
            action,
            field: fieldValue,
            description,
            entityId: entityId ?? '',
            entityName: entityName ?? '',
            parentEntitiyType: logContextType,
            parentEntitiyId: parentEntitiyId ?? '',
          };
          await saveActivityLog(logPayload);

          if (logContextType === LogContextType.Lead && clientContactId) {
            const contactLogPayload = {
              ...defaultLeadActivityLogDTO,
              contextData: {},
              action,
              field: 'Client Contact',
              description: `Marked Contact as DNC for Client Contact  ${entityName} under`,
              entityId: clientContactId,
              entityName: clientContactName,
              parentEntitiyType: LogContextType.Client,
              parentEntitiyId: parentEntitiyId ?? clientContactParentId,
            };

            await saveActivityLog(contactLogPayload);
          }
        }
      }

      setNotification(success('Updated Data Privacy'));
      window.location.reload();
    } catch (error) {
      setNotification(failed('Failed to Update Data Privacy'));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const request: DncLogSearchFilterDTO = {
        linkedCandidateEmails: [email],
      };
      const logsResponse = await getDncLogByFilter(request);
      const settingsResponse = await getCompanyGDPRSettingsByLoggedineRecruiter();
      setDataPrivacyState({
        logs: logsResponse.data,
        settings: settingsResponse.data,
      });
    };
    loadData();
  }, [email, workSpace.id]);
  return (
    <FormProvider {...methods}>
      <Box className="data-privacy" component="form" onSubmit={methods.handleSubmit(submit)}>
        <Grid container mt={2.5} mb={3}>
          <Grid item xs={7.8} ml={4} display="flex" columnGap={0.5}>
            <WsTypography
              color={WsTypoColors.QUINARY}
            >{`You are changing data privacy options for `}</WsTypography>
            <WsTypography color={WsTypoColors.PRIMARY}>{candidateName}</WsTypography>
          </Grid>
          <Grid item xs={2} display="flex" justifyContent="flex-end">
            <WsButton type="reset" variant="outlined">
              Cancel
            </WsButton>
          </Grid>
          <Grid item xs={1.5} display="flex" justifyContent="flex-end">
            <WsButton type="submit">Save</WsButton>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} ml={4}>
            <WsButtonGroup
              inputTitle={'Select Data Privacy Option:'}
              showMandatorySymbol={true}
              buttonsList={Array.from(options.keys())}
              defaultValue={Array.from(options.values()).indexOf(selectedOption)}
              selectedIndexValue={setDataPrivacyOptions}
              size="small"
            ></WsButtonGroup>
          </Grid>
          {selectedOption === 'restrict' && (
            <Grid item xs={12} ml={4}>
              <Label sx={{ mb: 0.5 }}> {restrictLabel ? restrictLabel : 'Restrict Up To *'}</Label>
              <WsDatePicker name="dncEndDate" label="" size="small" disablePast={true} />
            </Grid>
          )}
          <Grid item xs={12} ml={4} mt={1}>
            <ControlledTextArea
              placeholder="Add comments on why you are adding this to DNC"
              name="notes"
              labelText="Comment"
              showMandatorySymbol={true}
              minRows={3}
            />
          </Grid>
          <Grid item xs={12} ml={4} mt={1}>
            <Label sx={{ mb: 0.5 }}>Upload a File or Screenshot*</Label>
            <WsFileUpload
              name="attachment"
              placeholderText="Add a Screenshot of an email or any other document"
              onUpload={onFileUpload}
              fileTypes={FileTypeVariants.IMAGES}
              Icon={<></>}
            />
            {errors.attachmentUrl && errors.attachmentUrl.message && (
              <FormHelperText sx={{ fontSize: 15 }} error={true}>
                {errors.attachmentUrl.message}
              </FormHelperText>
            )}
            {dataPrivacyState.file?.name && (
              <FormHelperText>
                {dataPrivacyState.file?.name + ' '}
                <WsButton
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    methods.setValue('attachmentUrl', '');
                    setDataPrivacyState((previousData) => ({ ...previousData, file: null }));
                  }}
                >
                  Remove
                </WsButton>
              </FormHelperText>
            )}
          </Grid>
        </Grid>
        {dataPrivacyState.logs.length > 0 && (
          <>
            <Label sx={{ mt: 1, ml: 4 }}>Logs</Label>
            <Box my={2} height="230px" sx={{ overflow: 'hidden', overflowY: 'scroll' }}>
              <DNCLogs logs={dataPrivacyState.logs} />
            </Box>
          </>
        )}
      </Box>
    </FormProvider>
  );
};

export default DataPrivacy;
